/** "History" price popup on each line and the "sub-category median CM" button. */
import { f2, f3, pct } from "./num.ts";
import { L, type History, type Lang, type LineKind, type Sheet, type Src } from "./types.ts";

export interface PriceRec { c: string; px: number; code: string; d: string; sc: string; unit: string }
export interface LookupResult { recs: PriceRec[]; stats: { n: number; p25: number; med: number; p75: number } | null }

/**
 * Historical unit prices of one category (or all), filtered by words (AND over code, description, category, SC),
 * sorted cheapest first. `excludeZero` (偏差 D2, default on) drops $0 records like statsOf does; the original kept them.
 */
export function lookupPrices(h: History, kind: LineKind, cat: string, query: string, opts: { excludeZero?: boolean } = {}): LookupResult {
  const pool = kind === "fab" ? h.fabGlobal : h.trimGlobal;
  let recs: PriceRec[] = [];
  for (const c of cat ? [cat] : Object.keys(pool)) {
    const p = pool[c];
    if (!p) continue;
    for (const x of p.recs || []) {
      recs.push({
        c, px: x[0] as number, code: x[1] as string,
        d: kind === "fab" ? String(x[2] || "") + (x[4] ? " · " + x[4] : "") : String(x[2] || ""),
        sc: kind === "fab" ? String(x[3] || "") : "", unit: p.unit || "",
      });
    }
  }
  if (opts.excludeZero !== false) recs = recs.filter((x) => +x.px > 0);
  const qq = query.trim().toLowerCase();
  if (qq) recs = recs.filter((x) => { const hay = (x.code + " " + x.d + " " + x.c + " " + x.sc).toLowerCase(); return qq.split(/\s+/).every((w) => hay.indexOf(w) >= 0); });
  const prices = recs.map((x) => x.px);
  recs.sort((a, b) => a.px - b.px);
  return { recs, stats: recs.length ? { n: recs.length, p25: pct(prices, 0.25), med: pct(prices, 0.5), p75: pct(prices, 0.75) } : null };
}

/** Source note written on a line when a historical price is used. */
export function lookupTag(x: PriceRec, stats: LookupResult["stats"], lang: Lang): string {
  return x.c + " · " + (x.code || "") + (x.sc ? " · SC " + x.sc : "") + " · " + L(lang, "range in this search", "本次搜索区间") + " $" + f3(stats ? stats.p25 : 0) + "–" + f3(stats ? stats.p75 : 0);
}

/** Apply a looked-up record to a line: price (and optionally name + quality). Returns a new sheet. */
export function applyLookup(sheet: Sheet, kind: LineKind, i: number, x: PriceRec, stats: LookupResult["stats"], withName: boolean, lang: Lang): Sheet {
  const s = structuredClone(sheet);
  const r = s[kind][i];
  const tag = lookupTag(x, stats, lang);
  r.px = f3(x.px); delete r.cny; r.src.px = { t: "hist", d: tag }; // a USD price from history replaces one typed in RMB
  if (!r.cat) r.cat = x.c;
  r.flag = "";
  if (withName) { r.name = String(x.d).slice(0, 70) || x.code; r.qual = x.code || ""; r.src.name = { t: "hist", d: tag }; }
  return s;
}

export type CmMedianResult = { ok: true; v: string; src: Src; note: string } | { ok: false; error: string };

/**
 * "Sub-category median CM" (偏差 D5): sub-category from the AI result, else the LONGEST known sub-category
 * contained in the style name; falls back to the category median when the sub-category has no statistics.
 */
export function subCmMedian(sheet: Sheet, h: History, lang: Lang): CmMedianResult {
  let sub = sheet.ai && sheet.ai.sub && h.sub2cat[sheet.ai.sub] ? sheet.ai.sub : "";
  if (!sub) {
    const nm = (sheet.h.name || "").toUpperCase();
    for (const s of Object.keys(h.sub2cat)) if (nm.indexOf(s) >= 0 && s.length > sub.length) sub = s;
  }
  if (!sub) return { ok: false, error: L(lang, "Put the sub-category (e.g. CASUAL SHIRT) in Style name / category first.", "请先在「款名 / 品类」里写上子类，例如 CASUAL SHIRT。") };
  const cat = h.sub2cat[sub];
  const own = (h.sub[cat] || {})[sub];
  if (own && own.cm_med != null) {
    return { ok: true, v: f2(own.cm_med), src: { t: "hist", d: sub + " · " + L(lang, "median of", "历史中位数，共") + " " + own.n + " " + L(lang, "SCs", "个 SC") }, note: sheet.cm.note || sub + " median CM" };
  }
  const cs = h.cat[cat];
  if (cs && cs.cm_med != null) {
    return { ok: true, v: f2(cs.cm_med), src: { t: "hist", d: cat + " " + L(lang, "(category)", "（品类）") + " · " + L(lang, "median of", "历史中位数，共") + " " + cs.n + " " + L(lang, "SCs", "个 SC") }, note: sheet.cm.note || cat + " median CM" };
  }
  return { ok: false, error: L(lang, "No CM history for ", "没有 CM 历史：") + sub };
}
